import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRemarkSync } from 'react-remark';
import { TooltipContainer } from './Tooltip';
import { FloatingPortal } from '@floating-ui/react';
import { AnimatePresence } from 'framer-motion';
import { firmClientNamesDisplay } from '../../FirmClient';
import { LocalThemeContextProvider, useLocalThemeContext } from '../context';
import { AIContentEvalControls, AIContentEvalControlsProps } from './AIContentEvalControls';
import { useInteractions, useFloating, useHover, shift, offset, autoUpdate, safePolygon } from '@floating-ui/react';

interface ClientAISummaryTooltipProps {
  givenNamesDisplay: string;
  familyNamesDisplay: string;
  referenceElement: React.ReactNode;
  aiSummary: string | null | undefined;
  shortHouseholdCategorySummary?: string | null | undefined;
  referenceClassName?: string;
  title?: string;
  isSummaryLoading?: boolean;
  aiContentEvalControlsProps?: AIContentEvalControlsProps;
}

export function ClientAISummaryTooltip({
  title,
  aiSummary,
  isSummaryLoading,
  givenNamesDisplay,
  familyNamesDisplay,
  referenceElement,
  referenceClassName,
  aiContentEvalControlsProps,
  shortHouseholdCategorySummary,
}: ClientAISummaryTooltipProps) {
  const localTheme = useLocalThemeContext();
  const { t: tCommon } = useTranslation('common');
  const reactAiSummary = useRemarkSync(aiSummary ?? '');
  const reactShortHouseholdCategorySummary = useRemarkSync(shortHouseholdCategorySummary ?? '');
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const {
    refs,
    floatingStyles,
    context: floatingContext,
  } = useFloating({
    placement: 'right',
    strategy: 'fixed',
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    whileElementsMounted: autoUpdate,
    middleware: [shift({ crossAxis: true, padding: 40 }), offset(8)],
  });
  const hover = useHover(floatingContext, { handleClose: safePolygon(), delay: { close: 400, open: 100 } });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} className={referenceClassName}>
        {referenceElement}
      </div>

      <AnimatePresence>
        {isTooltipOpen && (
          <FloatingPortal>
            <LocalThemeContextProvider {...localTheme}>
              <div className="z-50" ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                <TooltipContainer
                  motionProps={{
                    exit: { opacity: 0, y: 4 },
                    animate: { opacity: 1, y: 0 },
                    initial: { opacity: 0, y: 4 },
                  }}
                  className="md:max-w-[550px] w-auto min-w-64 !rounded-lg overflow-y-auto max-h-[calc(100dvh_-_80px)] !p-0"
                >
                  <div className="sticky top-0 px-4 z-10 bg-darkMode-gray-dark pt-4 flex justify-between">
                    <div>
                      <p className="text-2xl font-bold text-white line-clamp-3">{title ?? tCommon('ai-summary')}</p>
                      <p className="text-lg font-medium text-darkMode-gray-light">
                        {firmClientNamesDisplay({ givenNamesDisplay, familyNamesDisplay })}
                      </p>
                    </div>
                  </div>
                  <div
                    className={clsx(isSummaryLoading && 'animate-pulse', 'p-4 prose prose-neutral dark:prose-invert')}
                  >
                    {shortHouseholdCategorySummary && (
                      <div className="mb-4">
                        <h4 className="text-lg font-semibold">Short Summary</h4>
                        {reactShortHouseholdCategorySummary}
                        <hr className="my-4" />
                      </div>
                    )}
                    {reactAiSummary}
                    {aiContentEvalControlsProps && (
                      <AIContentEvalControls
                        {...aiContentEvalControlsProps}
                        className={clsx('sticky bottom-4 mx-4 mt-4', aiContentEvalControlsProps.className)}
                      />
                    )}
                  </div>
                </TooltipContainer>
              </div>
            </LocalThemeContextProvider>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
}
